const SESSION_KEY = 'gotalottery.analytics.session';
const ALLOWED_PAGES = new Set(['/', '/contact', '/bases', '/faq', '/verPredio']);
let config = null;
let configPromise = null;
let gaLoadedFor = null;
let clarityLoadedFor = null;
let clarityRunning = false;
let queue = [];
let flushTimer = null;

export function publicAnalyticsPath(pathname) {
  if (ALLOWED_PAGES.has(pathname)) return pathname;
  if (/^\/(comprar|ticketsRifa)\/[^/]+$/.test(pathname)) return '/comprar/raffle';
  if (/^\/(tablero|boardRifa)\/[^/]+$/.test(pathname)) return '/tablero/raffle';
  return null;
}

export function getAnalyticsSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export async function getAnalyticsConfig() {
  if (config) return config;
  if (!configPromise)
    configPromise = fetch('/api/analytics/config')
      .then(response => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  config = await configPromise;
  return config;
}

function loadGa(measurementId) {
  if (!measurementId || gaLoadedFor === measurementId) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
  gaLoadedFor = measurementId;
}

function callClarity(command) {
  try {
    if (typeof window.clarity === 'function') window.clarity(command);
  } catch {
    /* optional tag */
  }
}

function loadClarity(projectId) {
  if (!projectId || clarityLoadedFor === projectId) return;
  const shim = function () {
    (shim.q = shim.q || []).push(arguments);
  };
  if (typeof window.clarity !== 'function') window.clarity = shim;
  const script = document.createElement('script');
  script.id = 'gotalottery-clarity';
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
  script.onerror = () => {};
  document.head.appendChild(script);
  clarityLoadedFor = projectId;
  clarityRunning = true;
}

export async function updateAnalyticsRoute(pathname) {
  const safePath = publicAnalyticsPath(pathname);
  if (!safePath) {
    if (clarityRunning) {
      callClarity('stop');
      clarityRunning = false;
    }
    return;
  }
  const settings = await getAnalyticsConfig();
  // Navigation may have changed while the runtime config request was pending.
  if (publicAnalyticsPath(window.location.pathname) !== safePath) return;
  if (settings.ga4MeasurementId) {
    loadGa(settings.ga4MeasurementId);
    window.gtag('event', 'page_view', {
      page_location: `${window.location.origin}${safePath}`,
      page_path: safePath,
      page_title: document.title,
    });
  }
  if (settings.clarityProjectId) {
    if (!clarityLoadedFor) loadClarity(settings.clarityProjectId);
    else if (!clarityRunning) {
      callClarity('start');
      clarityRunning = true;
    }
  }
}

const EVENT_NAMES = {
  raffle_view: 'view_item',
  ticket_selection: 'ticket_selection',
  checkout_start: 'begin_checkout',
  reservation_created: 'reservation_created',
  payment_method_selected: 'payment_method_selected',
};

export function trackAnalyticsEvent(type, data = {}) {
  const sessionId = getAnalyticsSessionId();
  if (!sessionId) return;
  const event = {
    eventId: crypto.randomUUID(),
    sessionId,
    type,
    ...(data.rifaId ? { rifaId: data.rifaId } : {}),
    ...(Number.isInteger(data.ticketCount) ? { ticketCount: data.ticketCount } : {}),
    ...(data.stage ? { stage: data.stage } : {}),
  };
  queue.push(event);
  if (EVENT_NAMES[type]) void sendGaFunnelEvent(type, data);
  if (!flushTimer) flushTimer = setTimeout(flushAnalyticsEvents, 250);
}

async function sendGaFunnelEvent(type, data) {
  const settings = await getAnalyticsConfig();
  if (settings.ga4MeasurementId) {
    loadGa(settings.ga4MeasurementId);
    const params =
      type === 'raffle_view' || type === 'checkout_start'
        ? { items: [{ item_id: data.rifaId, item_name: 'Rifa', quantity: data.ticketCount || 1 }] }
        : { item_id: data.rifaId, quantity: data.ticketCount };
    window.gtag('event', EVENT_NAMES[type], params);
  }
}

export async function flushAnalyticsEvents() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  if (!queue.length) return;
  const events = queue.splice(0, 20);
  try {
    await fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ events }),
    });
  } catch {
    /* observational only */
  }
  if (queue.length) flushTimer = setTimeout(flushAnalyticsEvents, 250);
}

function getGaValue(id, field) {
  return new Promise(resolve => {
    if (!id || typeof window.gtag !== 'function') return resolve(undefined);
    const timer = setTimeout(() => resolve(undefined), 1000);
    window.gtag('get', id, field, value => {
      clearTimeout(timer);
      resolve(value);
    });
  });
}

export async function getAnalyticsAttribution() {
  const settings = await getAnalyticsConfig();
  const [gaClientId, gaSessionId] = await Promise.all([
    getGaValue(settings.ga4MeasurementId, 'client_id'),
    getGaValue(settings.ga4MeasurementId, 'session_id'),
  ]);
  return { analyticsSessionId: getAnalyticsSessionId(), gaClientId, gaSessionId };
}
